import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/api';
import { CheckCircle, XCircle, Crown, Clock, ExternalLink, Copy, Check } from 'lucide-react';

const MCP_URL = 'https://api.vocabranch.com/mcp';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export const SettingsPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionParam = searchParams.get('subscription');

  const [isManaging, setIsManaging] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyMcpUrl = async () => {
    await navigator.clipboard.writeText(MCP_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showUpgradeButton = user?.plan !== 'premium';
  const showManageButton = user?.plan === 'premium';

  // Re-fetch user after returning from Stripe so plan state is up to date
  useEffect(() => {
    if (subscriptionParam === 'success' || subscriptionParam === 'cancelled') {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManage = async () => {
    setIsManaging(true);
    setActionError(null);
    try {
      const { url } = await apiClient.createPortalSession();
      window.location.href = url;
    } catch {
      setActionError('Failed to open billing portal. Please try again.');
      setIsManaging(false);
    }
  };

  const dismissBanner = () => {
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {subscriptionParam === 'success' && (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-4">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-green-800">Payment successful!</p>
            <p className="text-sm text-green-700 mt-0.5">
              Your account has been upgraded to Premium. If your plan hasn't updated yet, refresh the page.
            </p>
          </div>
          <button onClick={dismissBanner} className="text-green-600 hover:text-green-800">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {subscriptionParam === 'cancelled' && (
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
          <XCircle className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-gray-800">Checkout cancelled</p>
            <p className="text-sm text-gray-600 mt-0.5">No payment was taken. You can upgrade any time.</p>
          </div>
          <button onClick={dismissBanner} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Plan card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
          {user?.plan === 'premium' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
              <Crown className="h-3.5 w-3.5" />
              Premium
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
              <Clock className="h-3.5 w-3.5" />
              Trial
            </span>
          )}
        </div>

        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Plan</dt>
            <dd className="font-medium text-gray-900 capitalize">{user?.plan ?? '—'}</dd>
          </div>
          {user?.trial_expires_at && user?.plan !== 'premium' && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Trial expires</dt>
              <dd className="font-medium text-gray-900">{formatDate(user.trial_expires_at)}</dd>
            </div>
          )}
        </dl>

        {(showUpgradeButton || showManageButton) && (
          <div className="mt-6 border-t border-gray-100 pt-5 space-y-3">
            {actionError && <p className="text-sm text-red-600">{actionError}</p>}

            {showUpgradeButton && (
              <p className="text-sm text-gray-500 text-center py-1">
                Premium plan coming soon.
              </p>
            )}

            {showManageButton && (
              <button
                onClick={handleManage}
                disabled={isManaging}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                {isManaging ? 'Redirecting…' : 'Manage subscription'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* AI Integrations card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">AI Integrations</h2>
        <p className="text-sm text-gray-500 mb-4">
          Connect Vocabranch to Claude, ChatGPT, or any MCP-compatible AI client.
        </p>
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">MCP Server URL</p>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <span className="flex-1 text-sm font-mono text-gray-800 truncate select-all">{MCP_URL}</span>
            <button
              onClick={handleCopyMcpUrl}
              className="flex-shrink-0 flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              title="Copy URL"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Account card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Email</dt>
            <dd className="font-medium text-gray-900">{user?.email}</dd>
          </div>
          {(user?.first_name || user?.last_name) && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Name</dt>
              <dd className="font-medium text-gray-900">
                {[user.first_name, user.last_name].filter(Boolean).join(' ')}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};
